import { transformRows } from '../patientImport/transformRows';
import { normalizeImportedTime } from './normalizeTime';
import { applyScheduleDefaults } from './scheduleDefaults';
import { normalizeAppointmentType, CANONICAL_TYPES } from './normalizeType';

// Normalise the time column IN THE RAW CELLS, before transformRows runs.
//
// transformRows lists time_ in its TEXT_FIELDS, so it does String(cell).trim()
// on the value. Date columns escape that (they are parsed from the raw cell by
// coerce.parseDate) but time columns do not — and SheetJS with cellDates:true
// hands back a real JS Date for an .xlsx time cell, or a bare fraction-of-a-day
// number for an unformatted one. Stringifying either yields something like
// "Sat Dec 30 1899 10:45:00 GMT-0700 (PDT)", which no time parser can read, so
// the value was dropped and every imported appointment saved a NULL time_.
//
// Rewriting the cell here means transformRows only ever sees "HH:MM".
// Unreadable cells become null (transformRows then omits the field) and are
// COUNTED, so a silent drop can never hide this again.
function normalizeTimeColumn(rawRows, columnMap, dataStartRowIndex) {
  const spec = columnMap?.time_;
  let idx = null;
  if (typeof spec === 'number') {
    idx = spec;
  } else if (spec && Array.isArray(spec.sources)) {
    // A combine spec over one column is still a single cell; a genuine
    // multi-column combine is left to the text path below.
    const sources = spec.sources.filter((i) => i != null);
    if (sources.length === 1) [idx] = sources;
  }
  if (idx == null) return { rows: rawRows, unparsedTimeCount: 0 };

  let unparsedTimeCount = 0;
  const rows = rawRows.map((raw, i) => {
    if (i < dataStartRowIndex || !Array.isArray(raw)) return raw;
    const cell = raw[idx];
    if (cell == null || cell === '') return raw;
    const t = normalizeImportedTime(cell);
    if (!t) unparsedTimeCount += 1;
    // Copy — rawRows is React state upstream and must not be mutated.
    const copy = raw.slice();
    copy[idx] = t;
    return copy;
  });
  return { rows, unparsedTimeCount };
}

// Turn detected sheet rows into notes-c-shaped appointment rows.
//
// Returns held-back rows separately rather than dropping them: a row with no date
// cannot appear on any schedule, and a row with no name would resolve to a junk
// patient identity, so both are surfaced to the user instead of vanishing.
export function buildScheduleRows({
  rawRows,
  dataStartRowIndex,
  columnMap,
  nameFormat,
  fallbackDate = null,
  physicianName = '',
  defaultType = '',
  userId = null,
}) {
  const { rows: timeFixedRows, unparsedTimeCount } =
    normalizeTimeColumn(rawRows, columnMap, dataStartRowIndex);

  const { rows, stats } = transformRows({
    rawRows: timeFixedRows,
    dataStartRowIndex,
    columnMap,
    nameFormat,
    transforms: { parseDates: true, normalizePhones: true, onCoerceFail: 'null' },
    // Only stamp a date when the user explicitly chose a fallback; otherwise a
    // dateless row must stay visible as a problem, not quietly become today.
    defaultVisitDate: fallbackDate || undefined,
  });

  // Long "Event" phrases ("Follow Up - Sleep") become the clinical codes the
  // app understands. Recorded original -> code with counts so the review screen
  // can show exactly what the conversion did before anything is written.
  const typeConversions = new Map();
  let unmappedTypeCount = 0;

  const normalized = rows.map((row) => {
    const out = { ...row };
    if (out.time_ != null) {
      const t = normalizeImportedTime(out.time_);
      if (t) out.time_ = t;
      else delete out.time_; // an unreadable time is no time, never a guessed one
    }
    if (out.type) {
      const converted = normalizeAppointmentType(out.type);
      if (converted !== out.type) {
        const seen = typeConversions.get(out.type) || { code: converted, count: 0 };
        seen.count += 1;
        typeConversions.set(out.type, seen);
        out.type = converted;
      } else if (!CANONICAL_TYPES.includes(out.type)) {
        // Left exactly as the file wrote it — counted so an unrecognised
        // vocabulary is visible rather than quietly imported as free text.
        unmappedTypeCount += 1;
      }
    }
    return out;
  });

  const missingDate = normalized.filter((r) => !r.date_);
  const rowsWithDate = normalized.filter((r) => r.date_);

  return {
    rows: applyScheduleDefaults(rowsWithDate, { physicianName, defaultType, userId }),
    heldBack: {
      missingDate,
      // transformRows drops nameless rows itself and only gives back a count.
      missingNameCount: stats.skippedNoName,
      // Rows that HAD a time in the file which could not be read. They still
      // import — an appointment with no time beats no appointment — but the
      // count is surfaced rather than swallowed.
      unparsedTimeCount,
      // Types the file wrote that are neither canonical nor convertible.
      unmappedTypeCount,
    },
    // [{ from, to, count }] — what the type conversion actually did.
    typeConversions: [...typeConversions.entries()]
      .map(([from, { code, count }]) => ({ from, to: code, count }))
      .sort((a, b) => b.count - a.count),
    stats,
  };
}
